// Foundry Stomper (replaces `stomper`) — elite: Quakebreaker. Render-only art (ENEMY-REFORGE, P1 §2 #06, §4).
// Lower-body heavy: immense black-iron legs, cracked ivory knee guards, wide metal-soled feet with glowing copper
// ankle mechanisms; a low, compact torso with stub arms and twin exhausts. Slow, heavy steps.
// Wind-up: one leg lifts high and straight (knee locked) before the slam.
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate } from './kit';
import { armPhase, blob, drum, legPhase, spike } from './parts_heavy';

const HIP_Y = 1.85, LEG_SWING = 0.32;
const COPPER_GLOW = 0xff9a3c;

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();

  // ---- legs (the silhouette leader)
  for (const s of [-1, 1]) {
    const lift = s < 0; // the right leg (-X) lifts for the wind-up
    const leg = { pivot: [s * 0.62, HIP_Y, 0] as [number, number, number], swing: LEG_SWING, sphase: legPhase(s), raise: lift ? -1.1 : 0 };
    const x = s * 0.66;
    b.add(blob(0.36, s * 0.62, 1.9, 0, [1, 1, 1], 10, 6), { color: PAL.iron, ...leg });
    b.add(limb([s * 0.62, 1.9, 0], [x, 1.08, 0.18], 0.47, 0.37, 10), { color: PAL.iron, ...leg });
    b.add(at(plate(0.16, 0.62, 0.56, 0.05), s * 1.06, 1.5, 0.05, 0, 0, s * 0.08), { color: PAL.ironLight, ...leg });
    b.add(blob(0.27, x, 1.08, 0.18, [1, 1, 1], 8, 5), { color: PAL.copperDark, ...leg });
    // cracked ivory knee guard
    b.add(at(plate(0.48, 0.52, 0.2, 0.06), x, 1.1, 0.42, -0.15), { color: PAL.ivory, ...leg });
    b.add(at(plate(0.03, 0.34, 0.02, 0.005, 1), x + 0.06, 1.12, 0.535, -0.15, 0, 0.5), { color: PAL.black, ...leg });
    b.add(at(plate(0.14, 0.03, 0.02, 0.005, 1), x - 0.08, 1.04, 0.525, -0.15, 0, -0.2), { color: PAL.black, ...leg });
    if (elite) {
      // Quakebreaker: reinforced knee plates (stacked, with a forward ram spike)
      b.add(at(plate(0.82, 0.3, 0.18, 0.05, 1), x, 1.42, 0.4, -0.3), { color: PAL.ironLight, ...leg });
      b.add(spike(0.11, 0.4, x + s * 0.55, 1.2, 0.3, 0, 0, -s * Math.PI / 2), { color: PAL.copper, ...leg });
      b.add(at(plate(0.56, 0.2, 0.18, 0.05, 1), x, 0.82, 0.4, 0.2), { color: PAL.ivoryShade, ...leg });
      b.add(spike(0.12, 0.34, x, 1.12, 0.66, Math.PI / 2), { color: PAL.copper, ...leg });
    }
    // pillar shin widening to the ankle
    b.add(limb([x, 1.08, 0.16], [x, 0.32, 0], 0.33, 0.46, 10), { color: PAL.iron, ...leg });
    b.add(drum(0.45, 0.49, 0.2, x, 0.3, 0.02, 0, 0, 0, 12), { color: PAL.copperDark, ...leg });
    b.add(drum(0.5, 0.5, 0.04, x, 0.33, 0.02, 0, 0, 0, 12), { color: COPPER_GLOW, glow: true, ...leg });
    // wide metal-soled foot
    b.add(at(plate(0.98, 0.2, 1.06, 0.06), x, 0.11, 0.08), { color: PAL.ironLight, ...leg });
    b.add(at(plate(1.02, 0.06, 1.1, 0.02, 1), x, 0.03, 0.08), { color: PAL.black, ...leg });
    for (const t of [-0.28, 0, 0.28]) b.add(at(plate(0.22, 0.14, 0.2, 0.04, 1), x + t, 0.13, 0.62), { color: PAL.iron, ...leg });
    b.add(at(plate(0.6, 0.05, 0.05, 0.005, 1), x, 0.23, 0.5), { color: COPPER_GLOW, glow: true, ...leg });
    if (elite) {
      // massive copper heel pistons
      b.add(limb([x, 1.0, -0.26], [x, 0.38, -0.6], 0.16, 0.16, 10), { color: PAL.copper, ...leg });
      b.add(limb([x, 0.42, -0.6], [x, 0.2, -0.68], 0.1, 0.1, 8), { color: PAL.ironLight, ...leg });
      b.add(at(plate(0.66, 0.32, 0.36, 0.06), x, 0.17, -0.58), { color: PAL.copperDark, ...leg });
      b.add(at(plate(0.04, 0.4, 0.04, 0.005, 1), x, 0.7, -0.4, -0.35), { color: COPPER_GLOW, glow: true, ...leg });
    }
  }

  // ---- low, compact torso
  b.add(at(plate(1.12, 0.44, 0.78, 0.08), 0, 1.98, -0.02), { color: PAL.iron });
  b.add(at(plate(0.9, 0.56, 0.8, 0.1), 0, 2.44, -0.06), { color: PAL.ironLight });
  b.add(at(plate(0.66, 0.38, 0.08, 0.03), 0, 2.46, 0.36), { color: PAL.ivoryShade });
  b.add(at(plate(0.46, 0.08, 0.04, 0.005, 1), 0, 2.34, 0.41), { color: COPPER_GLOW, glow: true });
  // squat sensor head with a single lens
  b.add(blob(0.3, 0, 2.84, 0.12, [1.1, 0.62, 1], 10, 6), { color: PAL.iron });
  b.add(at(plate(0.44, 0.12, 0.1, 0.03), 0, 2.86, 0.4), { color: PAL.ivory });
  b.add(drum(0.07, 0.07, 0.04, 0, 2.86, 0.46, Math.PI / 2, 0, 0, 8), { color: PAL.teal, glow: true });
  // twin exhausts behind
  for (const s of [-1, 1]) {
    b.add(drum(0.1, 0.13, 0.62, s * 0.3, 2.95, -0.36, -0.12, 0, 0, 8), { color: PAL.iron });
    b.add(drum(0.085, 0.085, 0.04, s * 0.3, 3.26, -0.4, -0.12, 0, 0, 8), { color: COPPER_GLOW, glow: true });
  }
  // stub piston arms
  for (const s of [-1, 1]) {
    const arm = { pivot: [s * 0.58, 2.6, 0] as [number, number, number], swing: 0.22, sphase: armPhase(s), raise: 0.35 };
    b.add(blob(0.17, s * 0.6, 2.6, 0, [1, 1, 1], 8, 4), { color: PAL.copperDark, ...arm });
    b.add(limb([s * 0.62, 2.6, 0], [s * 0.74, 2.15, 0.1], 0.12, 0.1, 8), { color: PAL.iron, ...arm });
    b.add(at(plate(0.22, 0.24, 0.24, 0.05), s * 0.76, 2.0, 0.12), { color: PAL.copper, ...arm });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Foundry Stomper', build, stride: 1.6, bob: 0.05, outline: 0.045 };
